import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Module manifest
 *
 * Purpose: Drives the desktop header mega-menu - opens/closes submenus on hover
 *   and focus, sizes the submenu panel, and manages keyboard focus so Tab/Shift+Tab,
 *   ArrowDown, and Escape move and release focus correctly through the menu.
 *
 * Public API: The `<header-menu>` custom element (registered via
 *   `customElements.define('header-menu', HeaderMenu)`). No exported symbols; the
 *   element is consumed declaratively from markup.
 *
 * Upstream dependencies: `@theme/component` (Component base class), `@theme/utilities`
 *   (debounce, onDocumentLoaded, setHeaderMenuStyle), `@theme/events`
 *   (MegaMenuHoverEvent); standard DOM APIs (MutationObserver, ResizeObserver,
 *   compareDocumentPosition). Requires a `header-component` ancestor for activation.
 *
 * Downstream consumers: the `<script src="{{ 'header-menu.js' | asset_url }}">` tag
 *   in `blocks/_header-menu.liquid`, which renders the `<header-menu>` element.
 *
 * Failure modes: no-op when no `header-component` ancestor is present (open/activate
 *   guard on `this.headerComponent`). The keyboard escape path
 *   (`#focusNextElementAfterMenu`) blurs the active element to `<body>` when no
 *   focusable element follows the header. `#deactivate` may no-op while the menu is
 *   still pointer-hovered (overflow list/menu `:hover`), but focus still escapes on
 *   Tab. Pointer-driven close is grace-delayed (DEACTIVATE_DELAY, 300ms) via
 *   `#scheduleDeactivate`/`#closeTimer`, and both `deactivate()` (before scheduling)
 *   and the timer callback (immediately before firing) re-check whether keyboard
 *   focus currently rests inside the submenu - if so the close is skipped/never
 *   scheduled, so a mouse-only pointerleave can never blur a link the user is
 *   still keyboard-navigating. `#deactivate` itself stays unconditional and
 *   synchronous for Escape, outside-click, and the keyboard Tab-out path above -
 *   none of those wait on the grace period or the focus re-check, since they are
 *   deliberate user actions that must close immediately (or are already moving
 *   focus themselves). Escape's own `activeItem.focus()` return-to-trigger (in
 *   both `#documentKeydownListener` and the Escape branch of
 *   `#handleSubmenuKeydown`) is guarded by the synchronous `#returningFocus` flag
 *   so that internally-generated focus event cannot reopen the menu Escape just
 *   closed (WCAG 2.2 SC 1.4.13 Dismissible) - the flag is scoped to only those two
 *   call sites and does not suppress `activate()` for the Tab-out-to-next-item
 *   focus move, which must still open the next item's submenu. The
 *   focus/open/close DOM operations have no retry semantics and are
 *   idempotent; the submenu-height measurement path is the exception - see
 *   the VV-204 note below for its retry ladder and self-heal. The `--submenu-height`
 *   write inside `#openItem`'s MutationObserver callback is guarded against a
 *   stale (no-longer-active) submenu, since `disconnect()` cannot cancel a
 *   callback already mid-flight when a direct item-to-item hover swap occurs.
 *   VV-204: `deactivate()` deliberately does not cancel a pending debounced
 *   open (`#hoverTimer`) - a stray pointerleave (pointer jitter on a fast
 *   flick, or a layout shift under a stationary cursor as the previous panel
 *   collapses) must not strand the newly-entered item closed. The timer
 *   instead re-validates at fire time that the pointer still rests on the
 *   entered item (`:hover`) before opening, so a genuine leave-to-elsewhere
 *   still correctly results in no open; only re-entry onto a *different*
 *   item (via `activate()`) cancels and re-arms the timer.
 *
 * Performance: standard. The focus-escape walk is a single bounded
 *   `document.querySelectorAll` over focusables run once per qualifying keydown;
 *   submenu height measurement is rAF-batched and observer-driven. Hover-intent
 *   open is debounced (ACTIVATE_DELAY, 100ms) and survives stray pointerleaves
 *   without cancellation (re-validated against `:hover` at fire time - see
 *   VV-204 note above); close is grace-delayed
 *   (DEACTIVATE_DELAY, 300ms), each via a single shared timer that is cancelled
 *   and replaced (never accumulated) on every re-entry or re-schedule.
 *
 * VV-204: measurements taken from a not-yet-laid-out (content-visibility-skipped)
 *   submenu subtree are never committed as the final height - `#openItem`
 *   substitutes `PROVISIONAL_SUBMENU_HEIGHT`, the refresh pipeline and
 *   MutationObserver reject them structurally via `#isSubmenuContentUnrendered`,
 *   and `activate()` self-heals an already-committed degenerate height on the
 *   next re-hover via `#isPanelJammed`.
 *
 * VV-204 hydration-morph hazard: this component's runtime `aria-expanded`/
 *   `style` (on `<header-menu>` itself) and `data-active` (on the
 *   `[ref="submenu[]"]` div) are set outside the render cycle, so a
 *   background section-hydration morph (`assets/section-hydration.js` ->
 *   `assets/morph.js`) re-fetching this section would otherwise strip them
 *   from a live, possibly-open menu without calling `#deactivate()`. Both
 *   elements carry `data-skip-node-update="true"` in
 *   `blocks/_header-menu.liquid` to freeze their own attributes against that
 *   morph while still letting their children (the mega-menu grid content)
 *   hydrate normally - do not remove that attribute without re-checking
 *   `assets/morph.js`'s `data-skip-node-update` branch.
 */

/**
 * Hover-intent delay before a top-level item's submenu opens. VV-86
 * deliberately introduced this debounce so a fast horizontal sweep across
 * the top-level nav doesn't flash-open every submenu in between; VV-204
 * shortened it from 300ms (perceived as sluggish) while keeping the debounce.
 * VV-204 also made this pending open immune to a stray pointerleave -
 * `deactivate()` no longer cancels it - so a fast flick or a layout-shift
 * leave under a stationary cursor can't strand the item mid-swap; the timer
 * re-validates `:hover` on the entered item at fire time instead, so a
 * genuine leave-to-elsewhere still correctly does not open it.
 */
const ACTIVATE_DELAY = 100;

/**
 * Grace period before a hovered/focused submenu actually closes. Required
 * for WCAG 2.2 SC 1.4.13 (Content on Hover or Focus) "Hoverable" - the
 * pointer must be able to travel from the trigger into the submenu without
 * it disappearing first. A prior Horizon upstream sync (d5c5e03) deleted an
 * equivalent debounce entirely; re-check this constant survives future syncs.
 */
const DEACTIVATE_DELAY = 300;

/**
 * VV-204: cheap first gate below which a *committed* --submenu-height is
 * treated as suspicious by #isPanelJammed. This is NOT a validity oracle -
 * the merchant bottom-padding setting spans 0-100px, so real heights below
 * 48 and degenerate padding-only lies above 48 both exist. The structural
 * #isSubmenuContentUnrendered check does the actual disambiguation; this
 * just avoids a layout read on the overwhelmingly-common healthy re-hover.
 */
const MIN_SANE_SUBMENU_HEIGHT = 48;

/**
 * VV-204: height committed when a submenu is opened while its content
 * subtree is still layout-skipped (content-visibility:auto) and therefore
 * unmeasurable. Mirrors the CSS placeholder `contain-intrinsic-size: 0px
 * 500px` on .menu-list__submenu in sections/header.liquid (~line 1200) -
 * keep the two in sync. Committing this instead of the padding-only lie
 * means the panel is never aria-open yet visually closed; the refresh
 * pipeline snaps it to the real height (the clip-path transition animates
 * the correction) as soon as the subtree lays out.
 */
const PROVISIONAL_SUBMENU_HEIGHT = 500;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #hoverTimer = null;
  #closeTimer = null;
  #mutationObserverTimer = null;

  /**
   * True only for the synchronous duration of an internally-generated
   * `.focus()` call that returns focus to a trigger this component just
   * closed (Escape). `.focus()` dispatch is synchronous, so this flag is
   * only ever observed by the `activate()` invocation it itself triggers -
   * see #documentKeydownListener and the Escape branch of
   * #handleSubmenuKeydown, the only two call sites that set it.
   */
  #returningFocus = false;

  #submenuKeydownListener = (event) => {
    this.#handleSubmenuKeydown(event);
  };

  #documentClickListener = (event) => {
    if (!(event.target instanceof Node) || this.contains(event.target)) return;
    this.#deactivate();
  };

  #documentKeydownListener = (event) => {
    if (event.key !== 'Escape') return;

    const activeItem = this.#state.activeItem;
    this.#deactivate();

    // Escape must close the menu AND return focus to the trigger (WCAG 2.2
    // SC 1.4.13 Dismissible) without the resulting focus event reopening it.
    this.#returningFocus = true;
    activeItem?.focus();
    this.#returningFocus = false;
  };

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.addEventListener('click', this.#documentClickListener);
    document.addEventListener('keydown', this.#documentKeydownListener);
    this.addEventListener('keydown', this.#submenuKeydownListener);

    if (this.dataset.keepMegaMenuOpen === 'true') {
      requestAnimationFrame(() => this.#openDefaultItemForStyling());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    document.removeEventListener('click', this.#documentClickListener);
    document.removeEventListener('keydown', this.#documentKeydownListener);
    this.removeEventListener('keydown', this.#submenuKeydownListener);
    if (this.#hoverTimer) {
      clearTimeout(this.#hoverTimer);
      this.#hoverTimer = null;
    }
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#scheduleDeactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #openDefaultItemForStyling() {
    const defaultItem = this.querySelector(
      '.menu-list__list-item:not([slot="overflow"]):not([aria-hidden="true"]) > .menu-list__link[aria-haspopup="true"]'
    );

    if (defaultItem instanceof HTMLElement) {
      this.#openItem(defaultItem);
    }
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // An internally-generated focus-return-to-trigger (Escape) is not a user
    // landing on this item - it must not reopen the menu it just closed.
    if (this.#returningFocus) return;

    let item = findMenuItem(event.target);

    if (!item) return;

    // Whatever was scheduled for a previous item no longer applies now that
    // the pointer/focus is over `item`.
    if (this.#hoverTimer) {
      clearTimeout(this.#hoverTimer);
      this.#hoverTimer = null;
    }

    // A pending close must never win a race against re-entry: cancel it
    // before deciding anything else. This is what keeps the submenu
    // Hoverable (WCAG 2.2 SC 1.4.13) across a brief pointerleave/enter blip.
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    if (item == this.#state.activeItem && !this.#isPanelJammed(item)) {
      // Already open and healthy - cancelling the pending close above is
      // enough. Re-running #openItem on a healthy re-entry would cause a
      // visible re-animation flash, and a mid-load re-measure could even
      // replace a good height with a degenerate one - so bail in every case
      // except the VV-204 jam #isPanelJammed detects, where falling through
      // re-runs #openItem (debounced for pointer, immediate for focus) as the
      // user's natural retry.
      return;
    }

    const isDefaultSlot = item.slot === '' || item.slot == null;

    if (event instanceof FocusEvent) {
      this.dispatchEvent(new MegaMenuHoverEvent());
      this.#openItem(item, isDefaultSlot);
      return;
    }

    // The <li> the pointer actually entered (event.target is retargeted by the
    // component event-delegation proxy to the on:pointerenter-bearing <li>,
    // including the "More"/overflow <li>). Used to re-validate hover at fire
    // time - see the timer callback below.
    const enteredLi = event.target.closest('.menu-list__list-item');

    this.#hoverTimer = window.setTimeout(() => {
      this.#hoverTimer = null;

      // VV-204: re-validate the pointer still rests on the entered item before
      // opening. deactivate() no longer cancels this timer, so without this
      // guard a genuine leave-to-elsewhere during the delay could open an item
      // the pointer has already left. `:hover` reflects the live pointer
      // position at fire time (after any layout shift has settled), so a
      // cursor resting on the item - even one that emitted a spurious
      // pointerleave mid-delay - correctly still opens.
      // Rare edge: back-to-back layout shifts (e.g. pointerleave fires on the
      // first shift, then a second shift moves the li back under a stationary
      // cursor before the browser recomputes hover) can leave `:hover`
      // stale-false here, skipping an open the user's actual pointer position
      // would justify. Still strictly better than the old strand: state stays
      // consistent, the previous panel grace-closes cleanly, and the very
      // next pointer movement recomputes hover and opens the item.
      if (enteredLi && !enteredLi.matches(':hover')) return;

      // Cancel any close scheduled by an intervening leave so the fresh open
      // is not immediately grace-closed out from under the user.
      if (this.#closeTimer) {
        clearTimeout(this.#closeTimer);
        this.#closeTimer = null;
      }

      this.dispatchEvent(new MegaMenuHoverEvent());
      this.#openItem(item, isDefaultSlot);
    }, ACTIVATE_DELAY);
  };

  #openItem(item, isDefaultSlot = true) {
    if (!this.headerComponent) return;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      // Direct item-to-item swap: clear the previous item's content-visibility
      // marker here instead of routing through #deactivate(), which would zero
      // the shared header height and cause a visible close-then-open flash.
      const previouslyActiveSubmenu = findSubmenu(previouslyActiveItem);
      if (previouslyActiveSubmenu) {
        delete previouslyActiveSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Always tear down the previous item's observers before wiring up new
    // ones. #openItem can swap directly from an item with a submenu to a
    // plain link with none, in which case the `if (submenu)` block below
    // never runs and would otherwise leave the previous item's
    // ResizeObserver watching a now-hidden submenu until the next full close.
    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();

    const firstMenuItem = this.querySelector(
      '.menu-list__list-item:not([slot="overflow"]):not([aria-hidden="true"]) > .menu-list__link'
    );
    const contentAnchor = firstMenuItem instanceof HTMLElement ? firstMenuItem : item;
    const submenuContentStart = Math.max(0, Math.round(contentAnchor.getBoundingClientRect().left));
    this.style.setProperty('--submenu-content-start', `${submenuContentStart}px`);

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            // disconnect() cannot cancel a callback already mid-flight in this
            // rAF chain, so a still-pending callback from an item that has
            // since been swapped away from (direct item-to-item hover, no
            // close-then-open) can land after a *different* item opened and
            // overwrite its height with this now-stale measurement. Bail out
            // if `submenu` is no longer the active one - same guard shape as
            // #scheduleSubmenuHeightRefresh's refresh().
            const activeSubmenu = this.#state.activeItem ? findSubmenu(this.#state.activeItem) : null;
            if (activeSubmenu !== submenu || !this.headerComponent) return;

            const updatedHeight = this.#measureSubmenuHeight(submenu);
            // VV-204: only trust a measurement taken from a laid-out subtree.
            // Accepting the padding-only degenerate value here would not just
            // commit the lie - the success branch also disconnects this
            // observer, permanently killing the correction path. A still-
            // unrendered subtree keeps the observer alive for the next mutation.
            if (updatedHeight > 0 && !this.#isSubmenuContentUnrendered(submenu)) {
              this.headerComponent.style.setProperty('--submenu-height', `${updatedHeight}px`);
              this.#setFullOpenHeaderHeight(updatedHeight);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});
      this.#scheduleSubmenuHeightRefresh(submenu);

      // Auto-disconnect after 500ms to prevent memory leaks. Tracked in
      // #mutationObserverTimer so #cleanupMutationObserver (and therefore the
      // next #openItem / #deactivate) cancels it: an untracked timer from item
      // A would otherwise disconnect item B's freshly created observer
      // mid-flight on a quick A->B swap (VV-204).
      this.#mutationObserverTimer = window.setTimeout(() => {
        this.#mutationObserverTimer = null;
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu ? this.#measureSubmenuHeight(submenu) : 0;

    // VV-204: a measurement taken while the submenu's content has no layout
    // box is the content-visibility degenerate value (padding only), not a
    // real height. Committing it would leave the panel aria-open but visually
    // closed with state.activeItem set - the exact VV-204 jam. Open at the
    // provisional placeholder height instead; the refresh pipeline (RO fires
    // when the subtree lays out, plus the timer ladder) snaps it to the real
    // height. Scoped to the default-slot mega panel: overflow heights come
    // from the branch below and overflow submenus are content-visibility-
    // exempt, so they can never be "unrendered".
    if (isDefaultSlot && submenu && this.#isSubmenuContentUnrendered(submenu)) {
      finalHeight = PROVISIONAL_SUBMENU_HEIGHT;
    }

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Measure a submenu's visible height after layout.
   * @param {HTMLElement} submenu
   * @returns {number}
   */
  #measureSubmenuHeight(submenu) {
    const inner = submenu.querySelector('.menu-list__submenu-inner');

    if (inner instanceof HTMLElement) {
      const measuredContent = inner.querySelector('.mega-menu__grid') || inner.firstElementChild;
      const bottomPadding =
        Number.parseFloat(getComputedStyle(inner).getPropertyValue('--submenu-visible-bottom-padding')) || 28;

      if (measuredContent instanceof HTMLElement) {
        return Math.ceil(Math.max(measuredContent.offsetTop + measuredContent.offsetHeight + bottomPadding, 0));
      }

      return Math.ceil(Math.max(inner.offsetHeight, 0));
    }

    return Math.ceil(Math.max(submenu.offsetHeight, 0));
  }

  /**
   * True when the submenu's measurable content currently has no layout box:
   * its content-visibility:auto subtree has not been laid out yet, so any
   * height #measureSubmenuHeight returns right now is the padding-only
   * degenerate value (VV-204). A rendered-but-empty grid still gets its full
   * track width, so it never matches - only a skipped subtree zeroes both
   * dimensions. Returns false when the structure is missing (e.g. the
   * overflow shadow part or a plain link), so callers never gate on it there.
   * @param {HTMLElement} submenu
   * @returns {boolean}
   */
  #isSubmenuContentUnrendered(submenu) {
    const inner = submenu.querySelector('.menu-list__submenu-inner');
    if (!(inner instanceof HTMLElement)) return false;
    const content = inner.querySelector('.mega-menu__grid') || inner.firstElementChild;
    if (!(content instanceof HTMLElement)) return false;
    return content.offsetWidth === 0 && content.offsetHeight === 0;
  }

  /**
   * VV-204 self-heal detection: true when `item` is nominally open
   * (activeItem / aria-expanded / data-active all say so) but the committed
   * --submenu-height is a stale degenerate value, leaving the panel visually
   * closed with no observer left to correct it. Plain links without a submenu
   * legitimately commit 0px and must never be treated as jammed. Healthy
   * panels short-circuit on the committed-height gate without forcing a
   * layout read. Below the gate we only reopen when it can actually help:
   * either the subtree has since laid out and a fresh measure beats the
   * committed value, or it is still unrendered (reopening re-arms the
   * observers and commits the provisional height). A legitimately tiny
   * laid-out panel fails both arms, so re-hovering it never churns #openItem.
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  #isPanelJammed(item) {
    if (!this.headerComponent) return false;
    const submenu = findSubmenu(item);
    if (!submenu) return false;

    const committed = Number.parseFloat(this.headerComponent.style.getPropertyValue('--submenu-height'));
    const committedHeight = Number.isFinite(committed) ? committed : 0;
    if (committedHeight >= MIN_SANE_SUBMENU_HEIGHT) return false;

    return (
      this.#measureSubmenuHeight(submenu) > committedHeight + 1 || // +1 absorbs Math.ceil rounding
      this.#isSubmenuContentUnrendered(submenu)
    );
  }

  /**
   * Recalculate submenu height after late-loading images/fonts change layout.
   * @param {HTMLElement} submenu
   */
  #scheduleSubmenuHeightRefresh(submenu) {
    const refresh = () => {
      const activeSubmenu = this.#state.activeItem ? findSubmenu(this.#state.activeItem) : null;
      if (activeSubmenu !== submenu || !this.headerComponent) return;

      const updatedHeight = this.#measureSubmenuHeight(submenu);
      // VV-204: reject a measurement taken from an unrendered subtree so the
      // padding-only lie can't keep re-committing itself on every scheduled
      // refresh. Structural, not a static "< 48" floor: bottom padding is
      // merchant-configurable 0-100px, so a static floor would both miss
      // large-padding lies and permanently reject legitimate small heights.
      if (updatedHeight <= 0 || this.#isSubmenuContentUnrendered(submenu)) return;

      this.headerComponent.style.setProperty('--submenu-height', `${updatedHeight}px`);
      this.#setFullOpenHeaderHeight(updatedHeight);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(refresh);
    });
    window.setTimeout(refresh, 150);
    window.setTimeout(refresh, 400);
    // VV-204: bounded straggler retries for layouts that land after the 400ms
    // ladder (slow main thread during first-load hydration). Cheap, guarded
    // and idempotent like the timers above - the activeSubmenu guard in
    // refresh() neutralises any that fire after the item changed.
    window.setTimeout(refresh, 800);
    window.setTimeout(refresh, 1600);

    if ('ResizeObserver' in window) {
      this.#submenuResizeObserver = new ResizeObserver(refresh);
      this.#submenuResizeObserver.observe(submenu);

      const inner = submenu.querySelector('.menu-list__submenu-inner');
      if (inner instanceof HTMLElement) {
        this.#submenuResizeObserver.observe(inner);
      }
    }

    submenu.querySelectorAll('img').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;

      image.addEventListener('load', refresh, {once: true});
      image.addEventListener('error', refresh, {once: true});

      if (image.decode) {
        image.decode().then(refresh).catch(() => {});
      }
    });

    if ('fonts' in document) {
      document.fonts.ready.then(refresh).catch(() => {});
    }
  }

  /**
   * Deactivate the active item after a grace delay, unless the pointer/focus
   * target is demonstrably still within the menu (submenu or overflow menu),
   * or keyboard focus currently rests inside the submenu, in which case no
   * close is even scheduled.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    // VV-204: deliberately do NOT cancel a pending open (#hoverTimer) here.
    // A trailing pointerleave - from pointer jitter on a fast flick, or fired
    // when the previous panel collapses and shifts layout under a stationary
    // cursor - would otherwise strand the item the user just moved onto: its
    // debounced open gets cancelled and never re-armed, so the panel stays
    // stuck closed until another item is hovered. The open timer re-validates
    // that the pointer still rests on the intended item before firing (see
    // activate()), so a *genuine* leave still won't open an item the pointer
    // has left - it just isn't cancelled here by a spurious leave. Re-entry
    // onto a different item still cancels+re-arms the timer inside activate().
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    // A mouse-only pointerleave must never close a submenu that keyboard
    // focus currently rests inside (e.g. Tab/ArrowDown moved focus into a
    // submenu link before the mouse drifted off the trigger onto empty
    // page - relatedTarget in that case is unrelated, often <body>, and
    // can't tell us where focus actually is). This is deliberately a
    // static "where is focus right now" check, distinct from
    // isMovingToSubmenu below which tests where the pointer is *going*.
    const isFocusWithinMenu = menu?.contains(document.activeElement) ?? false;
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      (event.type === 'blur' || event.type === 'pointerleave') &&
      menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isFocusWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#scheduleDeactivate();
  }

  /**
   * Schedule the active item's close after DEACTIVATE_DELAY, replacing any
   * previously pending close. `activate()` cancels this timer on re-entry, so
   * a spurious pointerleave/pointerenter pair within the grace window never
   * closes the menu. See #deactivate for the immediate variant used by
   * Escape, outside-click, and the Tab-out handling in
   * #handleSubmenuKeydown (all three sub-cases: landing on the next
   * top-level item, escaping the header via #focusNextElementAfterMenu, or
   * the blur-to-<body> fallback) - none of those wait on this grace period
   * or the focus re-check below, since they are deliberate user actions
   * that must close immediately (or are already moving focus themselves)
   * regardless of current focus location.
   */
  #scheduleDeactivate() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
    }

    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = null;

      // Keyboard focus may have moved into the submenu *after* this close
      // was scheduled (mouse leaves first, then Tab/ArrowDown into the
      // still-open submenu before the grace period elapses). Re-check right
      // before closing so that case can't blur a link the user is actively
      // keyboard-navigating. Scoped to this timer callback only - #deactivate
      // itself stays unconditional so Escape/outside-click/Tab-out can still
      // close immediately even while they hold focus inside the submenu.
      const menu = findSubmenu(this.#state.activeItem);
      if (menu?.contains(document.activeElement)) return;

      this.#deactivate();
    }, DEACTIVATE_DELAY);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (this.dataset.keepMegaMenuOpen === 'true') return;

    if (this.#hoverTimer) {
      clearTimeout(this.#hoverTimer);
      this.#hoverTimer = null;
    }

    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #handleSubmenuKeydown(event) {
    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const submenu = findSubmenu(activeItem);
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;

    const topLevelItems = this.#getTopLevelMenuItems();
    const activeIndex = topLevelItems.indexOf(activeItem);

    if (target === activeItem && event.key === 'ArrowDown' && submenu) {
      const firstFocusable = this.#getFocusableElements(submenu)[0];
      if (firstFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
      return;
    }

    if (!submenu?.contains(target)) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#deactivate();

      // Same re-entrancy guard as #documentKeydownListener's Escape handling:
      // returning focus to the trigger must not reopen the menu it just closed.
      this.#returningFocus = true;
      activeItem.focus();
      this.#returningFocus = false;
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableItems = this.#getFocusableElements(submenu);
    if (focusableItems.length === 0) return;

    const firstFocusable = focusableItems[0];
    const lastFocusable = focusableItems[focusableItems.length - 1];

    if (event.shiftKey && target === firstFocusable) {
      event.preventDefault();
      activeItem.focus();
      return;
    }

    if (!event.shiftKey && target === lastFocusable) {
      event.preventDefault();
      const nextTopLevelItem = topLevelItems[activeIndex + 1];
      this.#deactivate();
      if (nextTopLevelItem) {
        nextTopLevelItem.focus();
      } else if (!this.#focusNextElementAfterMenu()) {
        // No focusable element follows the header; let the document take over.
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      }
    }
  }

  #getTopLevelMenuItems() {
    return Array.from(this.querySelectorAll('[ref="menuitem"]')).filter((item) => item instanceof HTMLElement);
  }

  #getFocusableElements(container) {
    return Array.from(container.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(
      (element) => element instanceof HTMLElement
    );
  }

  /**
   * Move focus to the next visible focusable element after this header menu in
   * document order, so Tab escapes the header instead of recycling to the parent.
   * Excludes elements contained within the menu (submenu links, the "More" button),
   * which return DOCUMENT_POSITION_CONTAINED_BY alongside FOLLOWING.
   * Note: the offsetParent === null visibility filter also excludes visible
   * position:fixed elements; worst case focus lands on the next non-fixed
   * focusable, which is still a valid forward escape.
   * @returns {boolean} true if focus was moved to a following element
   */
  #focusNextElementAfterMenu() {
    const FOLLOWING = Node.DOCUMENT_POSITION_FOLLOWING;
    const CONTAINED_BY = Node.DOCUMENT_POSITION_CONTAINED_BY;
    const candidates = Array.from(
      document.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
    const next = candidates.find((el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.offsetParent === null) return false;
      const position = this.compareDocumentPosition(el);
      return Boolean(position & FOLLOWING) && !(position & CONTAINED_BY);
    });
    if (next instanceof HTMLElement) {
      next.focus();
      return true;
    }
    return false;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    if (this.#mutationObserverTimer) {
      clearTimeout(this.#mutationObserverTimer);
      this.#mutationObserverTimer = null;
    }
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  #cleanupResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
